import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * La cámara.
 *
 * ── EL ERROR QUE HUNDE ESTOS FLUJOS ─────────────────────────────────────────────
 *
 * **"Denegado" NO es "todavía no preguntado".**
 *
 * En iOS, cuando alguien pulsa "No permitir", `getUserMedia` falla PARA SIEMPRE hasta que la
 * persona vaya a Ajustes → Safari → Cámara y lo cambie a mano. El navegador no vuelve a
 * preguntar nunca.
 *
 * O sea: un botón de "Permitir cámara" en esa pantalla no hace absolutamente nada. Se pulsa,
 * no pasa nada, se vuelve a pulsar, no pasa nada. La persona concluye que la aplicación está
 * rota — y tiene razón, porque le estamos ofreciendo una acción que no puede funcionar.
 *
 * Por eso `denegado` es un estado propio y su mensaje dice DÓNDE ir. Es la diferencia entre
 * alguien que resuelve en treinta segundos y alguien que abandona.
 *
 * ── Y EL ERROR QUE CASI NADIE PREVÉ: EL QR SUJETA LA CÁMARA ────────────────────
 *
 * Visto en un Samsung real el 18/07/2026: la persona escanea el QR **con la aplicación de
 * cámara del teléfono**, toca el enlace, y Chrome abre… mientras la app de cámara sigue viva
 * en segundo plano, sujetando el sensor (la luz verde en la barra del sistema lo delata).
 * Nuestro `getUserMedia` da `NotReadableError` — "ocupada" — y la persona jura, con razón,
 * que no hay ninguna otra aplicación usando la cámara. La que la usa es la que la trajo aquí.
 *
 * Android la suelta solo unos segundos después. Por eso `ocupada` se REINTENTA SOLA (cada
 * 3 s, hasta 5 veces): en el caso típico la pantalla se arregla delante de la persona sin
 * que toque nada. El botón de reintentar sigue ahí para el caso raro de una videollamada.
 */

export type FalloCamara =
  /** Dijo que no, o cerró el diálogo. En iOS esto es terminal: hay que ir a Ajustes. */
  | 'denegado'
  /** No hay cámara. Pasa en escritorios de sobremesa — y ahí es donde entra el QR. */
  | 'sin_camara'
  /** Otra aplicación la tiene ocupada. En móvil, casi siempre la app de cámara del QR. */
  | 'ocupada'
  /** Sin HTTPS no hay cámara. En producción no pasa; en local, todo el rato. */
  | 'sin_https'
  /** Cualquier otra cosa. */
  | 'desconocido';

export interface EstadoCamara {
  stream: MediaStream | null;
  fallo: FalloCamara | null;
  pidiendo: boolean;
}

/** Qué se le dice a la persona, y qué puede hacer al respecto. */
export function mensajeDelFallo(f: FalloCamara): { titulo: string; queHacer: string } {
  switch (f) {
    case 'denegado':
      return {
        titulo: 'No tenemos permiso para usar la cámara',
        // El texto más importante de esta pantalla. Sin él, la persona pulsa un botón que no
        // puede funcionar hasta que se rinde.
        queHacer:
          'Tu navegador tiene la cámara bloqueada para esta página. En iPhone: Ajustes → ' +
          'Safari → Cámara → Permitir. En Android: toca el candado de la barra de direcciones ' +
          'y activa la cámara. Después vuelve a abrir este enlace.',
      };
    case 'sin_camara':
      return {
        titulo: 'No encontramos una cámara',
        queHacer: 'Abre este enlace desde tu teléfono. Si estás en una computadora, usa el código QR.',
      };
    case 'ocupada':
      return {
        titulo: 'La cámara está ocupada',
        // El culpable típico es la app con la que se escaneó el QR. Nombrarla es la
        // diferencia entre "cierra otras aplicaciones" (¿cuáles? no hay ninguna) y algo que
        // la persona puede hacer de verdad. Y mientras lee esto, ya lo estamos reintentando.
        queHacer:
          'Si escaneaste el código QR con la cámara del teléfono, esa aplicación todavía la ' +
          'tiene en uso: ciérrala (o espera unos segundos — lo intentamos de nuevo solos). ' +
          'Si no, cierra otras aplicaciones que la estén usando, como videollamadas.',
      };
    case 'sin_https':
      return {
        titulo: 'Esta página no es segura',
        // Si esto sale en producción es un fallo NUESTRO, no de la persona. No hay nada que
        // pueda hacer, así que no se le pide que haga nada.
        queHacer: 'No podemos pedirte la cámara por una conexión insegura. Avisa a quien te envió el enlace.',
      };
    case 'desconocido':
      return {
        titulo: 'No pudimos abrir la cámara',
        queHacer: 'Cierra el navegador, vuelve a abrir el enlace e inténtalo de nuevo.',
      };
  }
}

/** Traduce el error del navegador. Los nombres son del estándar, no inventados. */
function clasificar(e: unknown): FalloCamara {
  if (!(e instanceof Error)) return 'desconocido';
  switch (e.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'denegado';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'sin_camara';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'ocupada';
    case 'SecurityError':
      return 'sin_https';
    default:
      return 'desconocido';
  }
}

export type Lado = 'documento' | 'rostro';

/** Cada cuánto y cuántas veces se reintenta sola una cámara ocupada. */
const MS_ENTRE_REINTENTOS_DE_OCUPADA = 3000;
const REINTENTOS_MAXIMOS_DE_OCUPADA = 5;

/**
 * Pide la cámara y devuelve el stream.
 *
 * ── EL STREAM SE APAGA SIEMPRE ─────────────────────────────────────────────────
 *
 * Si no se paran los tracks al desmontar, **la luz de la cámara se queda encendida** después
 * de que la persona termine. Eso es lo que hace que alguien cierre el navegador de golpe,
 * desinstale y no vuelva — y hace bien: una web que sigue mirándote cuando ya acabaste no
 * merece confianza.
 *
 * No es un detalle de higiene: es la diferencia entre un producto en el que se confía y uno
 * del que se huye.
 */
export function useCamara(lado: Lado): EstadoCamara & { reintentar: () => void } {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [fallo, setFallo] = useState<FalloCamara | null>(null);
  const [pidiendo, setPidiendo] = useState(false);
  const [intento, setIntento] = useState(0);
  const vivo = useRef(true);
  const reintentosDeOcupada = useRef(0);

  const reintentar = useCallback(() => setIntento((n) => n + 1), []);

  // La cámara ocupada se reintenta sola: el caso típico es la app de cámara del QR, que
  // Android suelta a los pocos segundos (ver la cabecera). Con tope, para no quedarnos
  // pidiendo la cámara en bucle contra una videollamada de una hora.
  useEffect(() => {
    if (fallo !== 'ocupada') {
      if (fallo === null) reintentosDeOcupada.current = 0;
      return;
    }
    if (reintentosDeOcupada.current >= REINTENTOS_MAXIMOS_DE_OCUPADA) return;

    const t = setTimeout(() => {
      reintentosDeOcupada.current += 1;
      reintentar();
    }, MS_ENTRE_REINTENTOS_DE_OCUPADA);
    return () => clearTimeout(t);
  }, [fallo, reintentar]);

  useEffect(() => {
    vivo.current = true;
    let actual: MediaStream | null = null;

    async function pedir(): Promise<void> {
      setPidiendo(true);
      setFallo(null);

      // `navigator.mediaDevices` no existe si no hay contexto seguro. Comprobarlo antes de
      // llamar evita un TypeError críptico y da el mensaje correcto.
      if (typeof navigator === 'undefined' || navigator.mediaDevices === undefined) {
        setFallo(window.isSecureContext === false ? 'sin_https' : 'sin_camara');
        setPidiendo(false);
        return;
      }

      // Para el documento, la trasera: la frontal de casi cualquier móvil no enfoca lo
      // bastante cerca como para leer un DNI.
      const ideal: MediaStreamConstraints = {
        audio: false,
        video:
          lado === 'documento'
            ? { facingMode: { ideal: 'environment' }, width: { ideal: 1920 } }
            : { facingMode: { ideal: 'user' }, width: { ideal: 1280 } },
      };

      try {
        actual = await navigator.mediaDevices.getUserMedia(ideal);
      } catch (e) {
        // OverconstrainedError: se pidió una trasera que no existe (una tablet, una webcam).
        // Reintentar sin restricciones en vez de morir: una cámara frontal es peor que la
        // trasera para leer un DNI, pero infinitamente mejor que ninguna.
        if (e instanceof Error && e.name === 'OverconstrainedError') {
          try {
            actual = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
          } catch (e2) {
            if (vivo.current) setFallo(clasificar(e2));
            setPidiendo(false);
            return;
          }
        } else {
          if (vivo.current) setFallo(clasificar(e));
          setPidiendo(false);
          return;
        }
      }

      // Si el componente se desmontó mientras la persona decidía, hay que apagar lo que se
      // acaba de encender. Sin esto, la luz de la cámara queda encendida en una pantalla que
      // ya no existe.
      if (!vivo.current) {
        actual.getTracks().forEach((t) => t.stop());
        return;
      }

      setStream(actual);
      setPidiendo(false);
    }

    void pedir();

    return () => {
      vivo.current = false;
      actual?.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
  }, [lado, intento]);

  return { stream, fallo, pidiendo, reintentar };
}

/**
 * Saca un fotograma en escala de grises, listo para `evaluarCalidad` del core.
 *
 * Se reduce a `anchoDestino` (240 por defecto) antes de medir, y no es por ahorrar: medir la
 * nitidez sobre 1920 píxeles de ancho, treinta veces por segundo, en un móvil viejo, calienta
 * el teléfono y tira los fotogramas al suelo — o sea, empeora la imagen que estamos midiendo.
 *
 * 240 de ancho basta de sobra: la nitidez y el brillo son propiedades de la imagen entera, no
 * del detalle fino.
 */
export function fotogramaEnGrises(
  video: HTMLVideoElement,
  lienzo: HTMLCanvasElement,
  anchoDestino = 240,
): { grises: Uint8Array; ancho: number; alto: number } | null {
  const wv = video.videoWidth;
  const hv = video.videoHeight;
  if (wv === 0 || hv === 0) return null; // el vídeo todavía no tiene fotogramas

  const ancho = Math.min(anchoDestino, wv);
  const alto = Math.round((hv / wv) * ancho);

  lienzo.width = ancho;
  lienzo.height = alto;

  // `willReadFrequently`: sin esto, Chrome mantiene el lienzo en la GPU y cada `getImageData`
  // fuerza una copia de vuelta que bloquea. Con treinta lecturas por segundo, se nota.
  const ctx = lienzo.getContext('2d', { willReadFrequently: true });
  if (ctx === null) return null;

  ctx.drawImage(video, 0, 0, ancho, alto);
  const datos = ctx.getImageData(0, 0, ancho, alto).data;

  const grises = new Uint8Array(ancho * alto);
  for (let i = 0, j = 0; i < datos.length; i += 4, j++) {
    // Luminancia perceptual (Rec. 601). Un promedio simple de R+G+B daría un verde brillante
    // y un azul brillante como iguales, y el ojo no los ve así.
    grises[j] = (datos[i]! * 299 + datos[i + 1]! * 587 + datos[i + 2]! * 114) / 1000;
  }

  return { grises, ancho, alto };
}
